(ns quuppa.qsm.planner.utils
  (:require [clojure.string :as str]))

;; Configuration
(def room-detection-service-url "http://localhost:8093")

(defn distance [[x1 y1] [x2 y2]]
  (Math/hypot (- x2 x1) (- y2 y1)))

(defn pixels-per-meter [points meters]
  (when (= 2 (count points))
    (/ (distance (first points) (second points)) meters)))

(defn rectangle-m [img-width img-height ppm]
  (let [ppm (if (and ppm (pos? ppm)) ppm 100)]
    {:x 0
     :y 0
     :width  (/ img-width ppm)
     :height (/ img-height ppm)}))

(defn translate-rect [rect dx dy]
  (-> rect
      (update :x + dx)
      (update :y + dy)))

(defn crop-rect [rect viewport]
  (let [x1 (max (:x rect) (:x viewport))
        y1 (max (:y rect) (:y viewport))
        x2 (min (+ (:x rect) (:width rect))
                (+ (:x viewport) (:width viewport)))
        y2 (min (+ (:y rect) (:height rect))
                (+ (:y viewport) (:height viewport)))]
    (when (and (< x1 x2) (< y1 y2))
      {:x x1 :y y1
       :width  (- x2 x1)
       :height (- y2 y1)})))


(defn make-rectangleM
  [{:keys [img-width img-height scale-points scale-distance]}]
  (let [ppm (or (pixels-per-meter scale-points scale-distance) 100)]
    (rectangle-m img-width img-height ppm)))

(defn rectM->css [rectM ppm]
  {:width  (str (* (:width rectM) ppm) "px")
   :height (str (* (:height rectM) ppm) "px")})

;; Distinct colors for simulation paths - easy to tell apart
(def route-colors
  ["#e41a1c"   ; red
   "#377eb8"   ; blue
   "#4daf4a"   ; green
   "#984ea3"   ; purple
   "#ff7f00"   ; orange
   "#a65628"   ; brown
   "#f781bf"   ; pink
   "#999999"   ; gray
   "#17becf"   ; cyan
   "#bcbd22"]) ; olive

;; Colors for geofences - using similar palette
(def geofence-colors
  ["#377eb8"   ; blue
   "#4daf4a"   ; green
   "#e41a1c"   ; red
   "#ff7f00"   ; orange
   "#984ea3"   ; purple
   "#17becf"   ; cyan
   "#a65628"   ; brown
   "#f781bf"   ; pink
   "#bcbd22"   ; olive
   "#999999"]) ; gray

;; Colors for units - using similar palette
(def unit-colors
  ["#4daf4a"   ; green
   "#ff7f00"   ; orange
   "#377eb8"   ; blue
   "#984ea3"   ; purple
   "#e41a1c"   ; red
   "#bcbd22"   ; olive
   "#17becf"   ; cyan
   "#f781bf"   ; pink
   "#a65628"   ; brown
   "#999999"]) ; gray

(defn get-route-color
  "Get color for a route - uses stored color or falls back to index-based."
  [route index]
  (or (:color route)
      (nth route-colors (mod index (count route-colors)))))

(defn get-geofence-color
  "Get color for a geofence - uses stored color or falls back to index-based."
  [geofence index]
  (or (:color geofence)
      (nth geofence-colors (mod index (count geofence-colors)))))

(defn get-unit-color
  "Get color for a unit - uses stored color or falls back to index-based."
  [unit index]
  (or (:color unit)
      (nth unit-colors (mod index (count unit-colors)))))

(defn point-to-segment-distance
  "Calculate distance from point [px py] to line segment [[x1 y1] [x2 y2]].
   Returns {:distance d :t t} where t is the interpolation parameter (0-1)."
  [[px py] [[x1 y1] [x2 y2]]]
  (let [dx (- x2 x1)
        dy (- y2 y1)
        len-sq (+ (* dx dx) (* dy dy))]
    (if (zero? len-sq)
      {:distance (js/Math.sqrt (+ (* (- px x1) (- px x1)) (* (- py y1) (- py y1))))
       :t 0}
      (let [t (max 0 (min 1 (/ (+ (* (- px x1) dx) (* (- py y1) dy)) len-sq)))
            proj-x (+ x1 (* t dx))
            proj-y (+ y1 (* t dy))
            dist (js/Math.sqrt (+ (* (- px proj-x) (- px proj-x))
                                  (* (- py proj-y) (- py proj-y))))]
        {:distance dist :t t}))))

(defn find-closest-segment
  "Find the segment closest to point [px py]. Returns {:segment-idx i :t t :distance d}.
   Includes the closing segment from last point back to first point."
  [points px py]
  (let [;; Include closing segment (last point to first point)
        segments (map vector points (concat (rest points) [(first points)]))]
    (->> segments
         (map-indexed (fn [idx seg]
                        (let [{:keys [distance t]} (point-to-segment-distance [px py] seg)]
                          {:segment-idx idx :t t :distance distance})))
         (apply min-key :distance))))

(defn generate-route-name
  "Generates a route name like 'sim001', 'sim002', etc. based on existing routes."
  [existing-routes]
  (let [existing-nums (->> existing-routes
                           (map (fn [[_ route]] (:name route)))
                           (filter #(and % (str/starts-with? % "sim")))
                           (map #(js/parseInt (subs % 3) 10))
                           (filter #(not (js/isNaN %)))
                           (set))
        next-num (loop [n 1]
                   (if (contains? existing-nums n)
                     (recur (inc n))
                     n))]
    (str "sim" (-> next-num str (.padStart 3 "0")))))

(defn generate-geofence-name
  "Generates a geofence name like 'geo001', 'geo002', etc. based on existing geofences."
  [existing-geofences]
  (let [existing-nums (->> existing-geofences
                           (map (fn [[_ geofence]] (:name geofence)))
                           (filter #(and % (str/starts-with? % "geo")))
                           (map #(js/parseInt (subs % 3) 10))
                           (filter #(not (js/isNaN %)))
                           (set))
        next-num (loop [n 1]
                   (if (contains? existing-nums n)
                     (recur (inc n))
                     n))]
    (str "geo" (-> next-num str (.padStart 3 "0")))))

(defn generate-unit-name
  "Generates a unit name like 'unit001', 'unit002', etc. based on existing units."
  [existing-units]
  (let [existing-nums (->> existing-units
                           (map (fn [[_ unit]] (:name unit)))
                           (filter #(and % (str/starts-with? % "unit")))
                           (map #(js/parseInt (subs % 4) 10))
                           (filter #(not (js/isNaN %)))
                           (set))
        next-num (loop [n 1]
                   (if (contains? existing-nums n)
                     (recur (inc n))
                     n))]
    (str "unit" (-> next-num str (.padStart 3 "0")))))

;; Helper function to extract localized label from IMDF LABEL format
(defn localized-label
  "Extract a display string from an IMDF LABEL object or plain string.
  LABEL format: {\"en\" \"Name\", \"fi\" \"Nimi\", ...}
  Uses 'en' as default locale, falls back to first available language."
  ([label-obj] (localized-label label-obj "en"))
  ([label-obj locale]
   (cond
     (nil? label-obj) nil
     (string? label-obj) label-obj
     (map? label-obj) (or (get label-obj locale)
                          (get label-obj "en")
                          (-> label-obj vals first))
     :else (str label-obj))))